/**
 * Lead bar welcome email
 *
 * Sends a friendly welcome email to a lead the moment they paste an email
 * into the sticky enrich bar. Includes a CTA to view their personalized
 * grants & earnings report and a CTA to book an Earnings Strategy Session
 * with the team via Calendly.
 *
 * Uses the existing email-server endpoint at `${REACT_APP_API_BASE}/sendEmail`
 * (Resend under the hood, from `RESEND_FROM_EMAIL`).
 *
 * Designed to be safe to call right before `window.location.href = ...`:
 *   - `keepalive: true` so the request survives the navigation
 *   - returns a Promise the caller can `await` for stricter ordering
 */

const DEFAULT_CALENDLY_URL =
	process.env.REACT_APP_CALENDLY_GRANTS_BOOKING_URL ||
	'https://calendly.com/d/2zv-t3h-gh5/preliminary-upgrade-assessment';

const DEFAULT_FROM = 'Will from Upgraded <will@getupgraded.ca>';
const DEFAULT_REPLY_TO = 'will@getupgraded.ca';

const PROD_APP_ORIGIN = 'https://app.getupgraded.ca';

function htmlSafe(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function getApiBase() {
	const envBase = process.env.REACT_APP_API_BASE;
	if (envBase && typeof envBase === 'string') {
		return envBase.replace(/\/+$/, '');
	}
	return '/api';
}

/**
 * Build the public app origin used inside the email body.
 *
 * - In the browser we default to `window.location.origin`, which keeps dev
 *   workflows (`http://localhost:3000`) honest while still pointing real
 *   prod sends at `https://app.getupgraded.ca`.
 * - When called from a non-browser context (or window is unset), we fall
 *   back to the production origin so the link is never broken.
 */
function getReportOrigin() {
	if (typeof window !== 'undefined' && window.location && window.location.origin) {
		return window.location.origin;
	}
	return PROD_APP_ORIGIN;
}

/**
 * Build the personalized report URL.
 *   `${origin}/grants-section?website=DOMAIN[&company=COMPANY]`
 */
export function buildReportUrl({ domain, companyName, origin } = {}) {
	const safeOrigin = (origin || getReportOrigin()).replace(/\/+$/, '');
	if (!domain) return safeOrigin;
	const params = new URLSearchParams();
	params.set('website', String(domain));
	if (companyName) {
		params.set('company', String(companyName));
	}
	return `${safeOrigin}/grants-section?${params.toString()}`;
}

function deriveFirstName(email) {
	if (!email || typeof email !== 'string' || !email.includes('@')) return '';
	const local = email.split('@')[0] || '';
	const firstChunk = local.split(/[._+-]/)[0] || '';
	if (!firstChunk) return '';
	const cleaned = firstChunk.replace(/\d+/g, '');
	if (!cleaned) return '';
	return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

/**
 * Build the HTML body. Inline styles only — Resend → Gmail/Outlook friendly.
 */
export function buildWelcomeEmailHtml({
	email,
	domain,
	companyName,
	reportUrl,
	calendlyUrl,
}) {
	const firstName = deriveFirstName(email);
	const greetingName = firstName || 'there';
	const headline = companyName
		? `Your ${htmlSafe(companyName)} earnings report is ready`
		: domain
			? `Your ${htmlSafe(domain)} earnings report is ready`
			: 'Your earnings report is ready';

	const safeReportUrl = htmlSafe(reportUrl);
	const safeCalendlyUrl = htmlSafe(calendlyUrl || DEFAULT_CALENDLY_URL);

	return `<!doctype html>
<html lang="en">
	<head>
		<meta charset="utf-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1" />
		<title>${htmlSafe(headline)}</title>
	</head>
	<body style="margin:0;padding:0;background-color:#F5F0EA;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,'Helvetica Neue',sans-serif;color:#1f2937;">
		<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">
			Your personalized grants &amp; earnings report is ready — book your strategy call.
		</div>
		<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F5F0EA;">
			<tr>
				<td align="center" style="padding:32px 16px;">
					<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="max-width:600px;background-color:#ffffff;border-radius:16px;box-shadow:0 8px 24px rgba(15,23,42,0.08);overflow:hidden;">
						<tr>
							<td style="padding:28px 28px 8px;">
								<div style="font-size:13px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:#3B6A9A;">
									Upgraded
								</div>
								<h1 style="margin:12px 0 0;font-size:26px;line-height:1.25;color:#0f172a;font-weight:700;">
									${htmlSafe(headline)} 🎉
								</h1>
							</td>
						</tr>
						<tr>
							<td style="padding:16px 28px 0;font-size:16px;line-height:1.55;color:#334155;">
								<p style="margin:0 0 14px;">Hi ${htmlSafe(greetingName)},</p>
								<p style="margin:0 0 14px;">
									Thanks for trying Upgraded! We just lined up your personalized
									<strong>grants &amp; earnings report</strong>${companyName ? ` for <strong>${htmlSafe(companyName)}</strong>` : ''} —
									qualified grants, vendor savings, AI automations and revenue
									forecasts, all in one place.
								</p>
								<p style="margin:0 0 14px;">
									Your report is live and ready to explore:
								</p>
							</td>
						</tr>
						<tr>
							<td align="center" style="padding:8px 28px 4px;">
								<a href="${safeReportUrl}" target="_blank" rel="noopener" style="display:inline-block;background-color:#3B6A9A;color:#ffffff;font-weight:600;font-size:16px;text-decoration:none;padding:14px 28px;border-radius:10px;box-shadow:0 4px 12px rgba(59,106,154,0.25);">
									View your earnings report →
								</a>
							</td>
						</tr>
						<tr>
							<td style="padding:24px 28px 0;font-size:16px;line-height:1.55;color:#334155;">
								<p style="margin:0 0 14px;">
									Want us to walk you through it? Book a complimentary
									<strong>Earnings Strategy Session</strong> with our team — we'll
									prioritize the highest-impact grants, savings and automations
									for your business and help you put a plan in motion.
								</p>
							</td>
						</tr>
						<tr>
							<td align="center" style="padding:8px 28px 4px;">
								<a href="${safeCalendlyUrl}" target="_blank" rel="noopener" style="display:inline-block;background-color:#0f766e;color:#ffffff;font-weight:600;font-size:16px;text-decoration:none;padding:14px 28px;border-radius:10px;box-shadow:0 4px 12px rgba(15,118,110,0.25);">
									Book your strategy session →
								</a>
							</td>
						</tr>
						<tr>
							<td style="padding:24px 28px 8px;font-size:14px;line-height:1.55;color:#475569;">
								<p style="margin:0 0 8px;">Or paste this link into your browser:</p>
								<p style="margin:0;word-break:break-all;">
									<a href="${safeReportUrl}" target="_blank" rel="noopener" style="color:#3B6A9A;text-decoration:none;">${safeReportUrl}</a>
								</p>
							</td>
						</tr>
						<tr>
							<td style="padding:24px 28px 28px;font-size:15px;line-height:1.55;color:#1f2937;">
								<p style="margin:0 0 6px;">Talk soon,</p>
								<p style="margin:0;font-weight:600;">Will &amp; the Upgraded team</p>
							</td>
						</tr>
					</table>
					<p style="max-width:600px;font-size:12px;line-height:1.5;color:#94a3b8;margin:16px auto 0;text-align:center;">
						You're getting this email because you submitted your details on
						getupgraded.ca. Reply to this email any time — a real human will get back to you.
					</p>
				</td>
			</tr>
		</table>
	</body>
</html>`;
}

function buildSubject({ companyName, domain }) {
	if (companyName) return `Your ${companyName} grants & earnings report is ready 🎉`;
	if (domain) return `Your ${domain} grants & earnings report is ready 🎉`;
	return 'Your grants & earnings report is ready 🎉';
}

/**
 * @param {Object} params
 * @param {string} params.email           recipient email (required)
 * @param {string} [params.domain]        normalized domain
 * @param {string} [params.companyName]   matched company display name
 * @param {string} [params.calendlyUrl]   override calendly URL
 * @param {string} [params.reportUrl]     override personalized report URL
 * @returns {Promise<{ ok: boolean, status?: number, skipped?: boolean, reason?: string, error?: string }>}
 */
export async function sendLeadBarWelcomeEmail({
	email,
	domain,
	companyName,
	calendlyUrl,
	reportUrl,
} = {}) {
	if (!email || typeof email !== 'string' || !email.includes('@')) {
		return { ok: false, skipped: true, reason: 'no_email' };
	}

	const finalReportUrl =
		reportUrl ||
		buildReportUrl({ domain, companyName });
	const finalCalendlyUrl = calendlyUrl || DEFAULT_CALENDLY_URL;

	const html = buildWelcomeEmailHtml({
		email,
		domain,
		companyName,
		reportUrl: finalReportUrl,
		calendlyUrl: finalCalendlyUrl,
	});

	const payload = {
		to: email,
		from: DEFAULT_FROM,
		replyTo: DEFAULT_REPLY_TO,
		subject: buildSubject({ companyName, domain }),
		html,
	};

	const url = `${getApiBase()}/sendEmail`;

	try {
		const res = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
			keepalive: true,
		});

		if (!res.ok) {
			const text = await res.text().catch(() => '');
			console.warn('[Lead bar welcome email] send failed', res.status, text);
			return { ok: false, status: res.status, error: text };
		}

		return { ok: true, status: res.status };
	} catch (err) {
		console.warn('[Lead bar welcome email] network error', err);
		return { ok: false, error: err?.message || String(err) };
	}
}
